import { CurrencyPipe } from '@angular/common';

const HUF_LOCALE = 'hu-HU';

function normalizeCurrency(currency: string | undefined): string {
  return (currency ?? 'EUR').toUpperCase();
}

function localeForCurrency(currency: string): string {
  if (currency === 'EUR') {
    return 'fr-FR';
  }
  if (currency === 'HUF') {
    return HUF_LOCALE;
  }
  return 'en-US';
}

function formatChfDecimals(absoluteValue: number, currencyPipe?: CurrencyPipe): string {
  const locale = localeForCurrency('CHF');

  if (currencyPipe) {
    const formatted = currencyPipe.transform(absoluteValue, 'CHF', 'code', undefined, locale) ?? '';
    return formatted.replace('CHF', 'CHF ');
  }

  // Same shape as CurrencyPipe code display: "CHF 12.50", including a space after CHF.
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CHF',
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absoluteValue);

  return formatted.replace(/\u00a0/g, ' ').replace(/CHF\s*/, 'CHF ');
}

function formatChf(value: number, currencyPipe?: CurrencyPipe): string {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);
  const isWholeNumber = Math.abs(absoluteValue - Math.round(absoluteValue)) < 0.01;

  if (isWholeNumber) {
    const intValue = Math.round(absoluteValue);
    return isNegative ? `- CHF ${intValue}.—` : `CHF ${intValue}.—`;
  }

  const withSpace = formatChfDecimals(absoluteValue, currencyPipe);
  return isNegative ? `- ${withSpace}` : withSpace;
}

function formatAed(value: number, currencyPipe?: CurrencyPipe): string {
  const locale = localeForCurrency('AED');
  const formatted = currencyPipe
    ? (currencyPipe.transform(value, 'AED', 'symbol', undefined, locale) ?? '')
    : new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'AED',
      }).format(value);

  // en currency pattern is `¤#,##0.00`, so CurrencyPipe renders the code glued to the amount (`AED12.50`).
  return formatted.replace(/\u00a0/g, ' ').replace(/AED\s*/, 'AED ');
}

/**
 * Formats a monetary amount for display according to marketplace currency rules.
 * @param amount Raw amount, or centimes when `amountInCents` is true.
 */
export function formatMarketplacePrice(
  amount: number,
  currency: string | undefined,
  options: {
    amountInCents?: boolean;
    currencyPipe?: CurrencyPipe;
  } = {},
): string {
  const normalizedCurrency = normalizeCurrency(currency);
  const value = options.amountInCents ? amount / 100 : amount;

  if (normalizedCurrency === 'HUF') {
    return new Intl.NumberFormat(HUF_LOCALE, {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(Math.round(value));
  }

  if (normalizedCurrency === 'CHF') {
    return formatChf(value, options.currencyPipe);
  }

  if (normalizedCurrency === 'AED') {
    return formatAed(value, options.currencyPipe);
  }

  const locale = localeForCurrency(normalizedCurrency);
  if (!options.currencyPipe) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
    }).format(value);
  }

  return (
    options.currencyPipe.transform(value, normalizedCurrency, 'symbol', undefined, locale) ?? ''
  );
}

/** Display symbol for UI labels (e.g. input suffix), not full formatted prices. */
export function getMarketplaceCurrencySymbol(currency: string | undefined): string {
  const normalizedCurrency = normalizeCurrency(currency);

  if (normalizedCurrency === 'EUR') {
    return '€';
  }
  if (normalizedCurrency === 'CHF') {
    return 'CHF';
  }
  if (normalizedCurrency === 'HUF') {
    return 'Ft';
  }

  return normalizedCurrency;
}
